import { K8sProviderIcon } from '@cast/features-cluster-ui';
import { useRef } from 'react';

import { useModal } from 'mui-modal-provider';

import { CAST_AI } from '@cast/constants';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Divider,
  PreviewSnippet,
  notify,
  ShowModalOutput,
  CloseDialogButton,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { Cluster } from '@cast/types';

type Props = DialogProps & {
  cluster: Cluster;
  onClose: () => void;
};

const DisconnectPhase1ClusterDialog = ({
  cluster,
  onClose,
  ...props
}: Props) => {
  return (
    <Dialog {...props} onClose={onClose} testId="phase1-disconnect-dialog">
      <DialogTitle sx={{ p: '16px 32px' }}>
        <Stack gap={24}>
          <Stack direction="row">
            <Stack direction="row" gap={4}>
              <K8sProviderIcon provider={cluster.providerType} size={24} />
              <Typography
                variant="A1"
                color={tokens.theme.fg.structural.primary}
              >
                {cluster.name}
              </Typography>
            </Stack>

            <CloseDialogButton onClose={onClose} />
          </Stack>

          <Stack gap={8}>
            <Typography variant="h4" color={tokens.theme.fg.structural.primary}>
              Disconnect your cluster
            </Typography>
            <Typography
              variant="P14R"
              color={tokens.theme.fg.structural.secondary}
            >
              To disconnect your cluster remove {CAST_AI} agent.
            </Typography>
          </Stack>
        </Stack>
      </DialogTitle>
      <Divider color={tokens.theme.border.structural.neutralSubtle} />
      <DialogContent sx={{ p: '16px 32px 0px' }}>
        <Stack gap={16}>
          <Stack gap={8}>
            <Stack gap={6}>
              <Typography
                variant="A1"
                color={tokens.theme.fg.structural.primary}
              >
                Agent removal script
              </Typography>
              <Typography
                variant="P12R"
                color={tokens.theme.fg.structural.secondary}
              >
                Ensure that kubectl is installed and the cluster is accessible.
                Copy the provided script and run it on a local terminal or cloud
                shell.
              </Typography>
            </Stack>
          </Stack>

          <PreviewSnippet
            testId="phase1-offboarding-script"
            code="kubectl delete namespace castai-agent"
            onCopy={() => notify.success('Copied to clipboard!')}
            p="8px 16px"
          />

          <Alert variant="filled" color="info">
            <Typography variant="P12R">
              The cluster will be disconnected immediately, however it might
              take{' '}
              <Typography variant="P12B" component="span">
                up to 5 min
              </Typography>{' '}
              for status to change in the console.
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: '16px 32px' }}>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="secondary"
            onClick={onClose}
            testId="close-dialog-action"
          >
            Close
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export const useDisconnectPhase1ClusterDialog = () => {
  const { showModal } = useModal();
  const modalInstance = useRef<ShowModalOutput>(undefined);

  return {
    openDisconnectPhase1ClusterDialog: (cluster: Cluster) => {
      modalInstance.current = showModal(
        DisconnectPhase1ClusterDialog,
        {
          cluster,
          onClose: () => modalInstance.current?.destroy(),
        },
        { rootId: 'root' }
      );
    },
  };
};
